import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  TextField,
  Button,
  Typography,
  ToggleButtonGroup,
  ToggleButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Avatar,
  CircularProgress,
  DialogContent,
  DialogActions
} from '@mui/material';
import {
  Business as BrandIcon,
  Add as AddIcon,
  Save as SaveIcon
} from '@mui/icons-material';
import PlatformDialog from '../PlatformDialog/PlatformDialog';
import PlatformDialogTitle from '../PlatformDialog/PlatformDialogTitle';

const TierProfileInfo = {
  small: { label: 'Pequena', emoji: '🏪', description: 'Até 10k seguidores' },
  medium: { label: 'Média', emoji: '🏢', description: 'Menos de 100k seguidores' },
  large: { label: 'Grande', emoji: '🏛️', description: 'Acima de 100k seguidores' }
};

const BrandFormDialog = ({
  open,
  onClose,
  onSubmit,
  isCreating = false,
  isAdminOrMensure = false,
  allUsers = [],
  initialData = null
}) => {
  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    userId: '',
    tierProfile: 'medium'
  });

  useEffect(() => {
    if (open) {
      if (initialData) {
        setFormData({
          name: initialData.name || '',
          logo: initialData.logo || '',
          userId: initialData.userId?._id || initialData.userId || '',
          tierProfile: initialData.tierProfile || 'medium'
        });
      } else {
        setFormData({
          name: '',
          logo: '',
          userId: '',
          tierProfile: 'medium'
        });
      }
    }
  }, [open, initialData]);

  const handleChange = (field) => (e) => {
    const value = e.target?.value !== undefined ? e.target.value : e;
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;
    if (isAdminOrMensure && !formData.userId) return;
    
    onSubmit(formData);
  };

  const isEdit = !!initialData;

  return (
    <PlatformDialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
    >
      <form onSubmit={handleSubmit}>
        <PlatformDialogTitle
          title={isEdit ? 'Editar Marca' : 'Nova Marca'}
          subtitle={isEdit ? `Atualize as configurações de ${formData.name}` : 'Cadastre uma nova marca para monitoramento'}
          icon={<BrandIcon />}
          onClose={onClose}
        />
        
        <DialogContent sx={{ pt: 3, pb: 2, px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {isAdminOrMensure && (
              <FormControl fullWidth required size="small">
                <InputLabel>Usuário / Cliente *</InputLabel>
                <Select
                  value={formData.userId}
                  label="Usuário / Cliente *"
                  onChange={handleChange('userId')}
                  sx={{ borderRadius: 2 }}
                >
                  {allUsers.map(u => (
                    <MenuItem key={u._id} value={u._id}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                        <Avatar sx={{ width: 24, height: 24, fontSize: '0.7rem', bgcolor: 'primary.main', fontWeight: 700 }}>
                          {u.name?.charAt(0)?.toUpperCase()}
                        </Avatar>
                        <Box>
                          <Typography variant="body2" fontWeight={500}>{u.name}</Typography>
                          <Typography variant="caption" color="text.secondary">{u.email}</Typography>
                        </Box>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <TextField
              autoFocus={!isAdminOrMensure}
              fullWidth
              label="Nome da Marca *"
              value={formData.name}
              onChange={handleChange('name')}
              placeholder="Ex: Nike Brasil"
              size="small"
              sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
            />

            {isAdminOrMensure && (
              <TextField
                fullWidth
                label="URL do Logo (opcional)"
                value={formData.logo}
                onChange={handleChange('logo')}
                placeholder="https://..."
                size="small"
                sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
              />
            )}

            <Box>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom sx={{ mb: 1, fontWeight: 600 }}>
                Perfil de Tier
              </Typography>
              <ToggleButtonGroup
                value={formData.tierProfile}
                exclusive
                onChange={(e, val) => val && handleChange('tierProfile')(val)}
                fullWidth
                size="small"
                sx={{ 
                  borderRadius: 2,
                  '& .MuiToggleButton-root': {
                    borderRadius: 2,
                    mx: 0.5,
                    border: '1px solid !important',
                    borderColor: 'divider !important',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                      '&:hover': { bgcolor: 'primary.dark' }
                    }
                  }
                }}
              >
                {Object.entries(TierProfileInfo).map(([key, info]) => (
                  <ToggleButton key={key} value={key} sx={{ flexDirection: 'column', py: 1.5 }}>
                    <span style={{ fontSize: '1.4rem', marginBottom: 4 }}>{info.emoji}</span>
                    <Typography variant="caption" fontWeight={700}>{info.label}</Typography>
                    <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.8 }}>{info.description}</Typography>
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
          <Button 
            onClick={onClose}
            sx={{ fontWeight: 600, textTransform: 'none' }}
          >
            Cancelar
          </Button>
          <Button
            type="submit"
            variant="contained"
            disabled={isCreating || !formData.name.trim() || (isAdminOrMensure && !formData.userId)}
            startIcon={isCreating ? <CircularProgress size={16} color="inherit" /> : (isEdit ? <SaveIcon /> : <AddIcon />)}
            sx={{ 
              fontWeight: 700, 
              textTransform: 'none',
              px: 3,
              borderRadius: 2,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' }
            }}
          >
            {isCreating ? 'Processando...' : (isEdit ? 'Salvar Alterações' : 'Criar Marca')}
          </Button>
        </DialogActions>
      </form>
    </PlatformDialog>
  );
};

BrandFormDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isCreating: PropTypes.bool,
  isAdminOrMensure: PropTypes.bool,
  allUsers: PropTypes.array,
  initialData: PropTypes.object
};

export default BrandFormDialog;
